import { Box, Stack, useTheme } from '@mui/material';
import {
  Book,
  Bookmark,
  CircleDollarSign,
  PenLine,
  Sparkles,
  SquarePen,
  User,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { H8 } from '../../components/Typography';
import FlexBox from '../../components/flexbox/FlexBox';
import { mypageCardDatasets } from './MypageCardData';

const MypageMenuCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // Format number to display as "1k", "2k", etc. for numbers >= 1000
  const formatCount = count => {
    const numCount = Number(count);
    if (isNaN(numCount)) return count; // Return as-is if not a number
    if (numCount >= 1000) {
      const value = numCount / 1000;
      const formatted = value.toFixed(1);
      // Remove ".0" if present, otherwise keep the decimal
      return formatted.endsWith('.0')
        ? `${Math.floor(value)}k`
        : `${formatted}k`;
    }
    return count;
  };

  // Menu items based on the image and MypageCardData
  const menuItems = [
    {
      id: 'mypage',
      title: '마이페이지',
      icon: User,
      route: '/naver/callmetellme/mypage',
      color: '#6B7280',
      count: null,
    },
    {
      id: 'subscribedProducts',
      title: '구독중인 상품',
      icon: CircleDollarSign,
      route: mypageCardDatasets.subscribedProducts.navigationRoute,
      color: '#6B7280',
      count: mypageCardDatasets.subscribedProducts.count,
    },
    {
      id: 'aiRecommendedSupportPrograms',
      title: 'AI 추천 지원사업',
      icon: Sparkles,
      route: mypageCardDatasets.aiRecommendedSupportPrograms.navigationRoute,
      color: '#6B7280',
      count: mypageCardDatasets.aiRecommendedSupportPrograms.count,
    },
    {
      id: 'aiRecommendedPolicyFunds',
      title: 'AI 추천 정책자금',
      icon: Sparkles,
      route: mypageCardDatasets.aiRecommendedPolicyFunds.navigationRoute,
      color: '#6B7280',
      count: mypageCardDatasets.aiRecommendedPolicyFunds.count,
    },
    {
      id: 'bookmarkedSupportPrograms',
      title: '북마크한 지원사업',
      icon: Bookmark,
      route: mypageCardDatasets.bookmarkedSupportPrograms.navigationRoute,
      color: '#6B7280',
      count: mypageCardDatasets.bookmarkedSupportPrograms.count,
    },
    {
      id: 'bookmarkedPosts',
      title: '북마크한 게시글',
      icon: Book,
      route: mypageCardDatasets.bookmarkedPosts.navigationRoute,
      color: '#6B7280',
      count: mypageCardDatasets.bookmarkedPosts.count,
    },
    {
      id: 'myPosts',
      title: '내가 쓴 게시글',
      icon: SquarePen,
      route: mypageCardDatasets.myPosts.navigationRoute,
      color: '#6B7280',
      count: mypageCardDatasets.myPosts.count,
    },
    {
      id: 'myComments',
      title: '내가 쓴 댓글',
      icon: PenLine,
      route: mypageCardDatasets.myComments.navigationRoute,
      color: '#6B7280',
      count: mypageCardDatasets.myComments.count,
    },
    {
      id: 'repliesToMyComments',
      title: '내 댓글에 답글',
      icon: PenLine,
      route: mypageCardDatasets.repliesToMyComments.navigationRoute,
      color: '#6B7280',
      count: mypageCardDatasets.repliesToMyComments.count,
    },
    {
      id: 'commentsOnMyPosts',
      title: '내 게시글에 달린 댓글',
      icon: PenLine,
      route: mypageCardDatasets.commentsOnMyPosts.navigationRoute,
      color: '#6B7280',
      count: mypageCardDatasets.commentsOnMyPosts.count,
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '24px',
        padding: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid #f0f0f0',
      }}
    >
      <Stack spacing={1}>
        {menuItems.map(menuItem => {
          const IconComponent = menuItem.icon;
          const isActive = location.pathname === menuItem.route;
          return (
            <FlexBox
              key={menuItem.id}
              onClick={() => navigate(menuItem.route)}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '8px 8px',
                borderRadius: '8px',
                transition: 'background-color 0.2s ease',
                backgroundColor: isActive ? '#f0f4ff' : 'transparent',
                '&:hover': {
                  backgroundColor: isActive ? '#f0f4ff' : '#f8f9fa',
                },
              }}
            >
              <FlexBox sx={{ alignItems: 'center', flex: 1 }}>
                <IconComponent
                  size={20}
                  style={{
                    color: isActive ? '#3F68FF' : menuItem.color,
                    marginRight: '12px',
                    flexShrink: 0,
                  }}
                />
                <H8
                  sx={{
                    color: isActive ? '#3F68FF' : menuItem.color,
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {menuItem.title}
                </H8>
              </FlexBox>
              {menuItem.count !== null && (
                <Box
                  sx={{
                    backgroundColor: theme.palette.primary.mainBlue,
                    color: '#ffffff',
                    borderRadius: '12px',
                    padding: '2px 10px',
                    fontSize: '12px',
                    fontWeight: 600,
                    minWidth: '24px',
                    textAlign: 'center',
                    marginLeft: '8px',
                  }}
                >
                  {formatCount(menuItem.count)}
                </Box>
              )}
            </FlexBox>
          );
        })}
      </Stack>
    </Box>
  );
};

export default MypageMenuCard;
